import { useEffect, useId, useRef } from "react";

import type { Organization } from "@/lib/types";

import { NewBusinessForm } from "./NewBusinessForm";

/**
 * Figma "Add a new business": the form card over a blurred, tinted scrim on top of the portal.
 * A native modal <dialog> keeps keyboard focus inside it; focus returns to the opener on close.
 */
export function NewBusinessModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (org: Organization) => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  useEffect(() => {
    const d = ref.current;
    const active = document.activeElement;
    const opener = active instanceof HTMLElement ? active : null;
    if (d && !d.open) {
      if (typeof d.showModal === "function") d.showModal();
      else d.setAttribute("open", ""); // environments without <dialog> support
    }
    return () => {
      if (opener && document.contains(opener)) opener.focus();
    };
  }, []);

  return (
    <dialog
      ref={ref}
      onClose={onClose}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      aria-labelledby={titleId}
      className="fixed inset-0 m-0 flex size-full max-h-none max-w-none overflow-y-auto bg-transparent p-4 backdrop:bg-transparent"
    >
      {/* A mouse shortcut: keyboard users close with Escape. */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div className="fixed inset-0 bg-[rgba(34,28,25,0.28)] backdrop-blur-[8px]" onClick={onClose} />
      <div className="relative m-auto w-full max-w-[560px] rounded-[28px] bg-surface p-6 drop-shadow-[0px_16px_20px_rgba(13,8,5,0.28)] sm:p-12">
        <NewBusinessForm titleId={titleId} onCreated={onCreated} />
      </div>
    </dialog>
  );
}
